import { State } from '../../../redux/reducers';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { IFrameComponent, iframeResizer } from 'iframe-resizer';
import { Store, select } from '@ngrx/store';
import { getDirectLoginProfile, WindowCommunicatorService, IWindowMessage } from '@uxdf/direct-shared-lib';
import { Subscription, asyncScheduler } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { SafeUrl, DomSanitizer } from '@angular/platform-browser';
import { map, filter, tap, observeOn, take } from 'rxjs/operators';
import { BookService } from '../book.service';
import { BookMessageTypes } from '../../../modules/parser/models/message.model';

@Component({
  standalone: false,
  selector: 'app-e-power',
  templateUrl: './e-power.component.html',
  styleUrls: ['./e-power.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EPowerComponent implements OnInit, OnDestroy {
  iframeSrc: SafeUrl;
  /* eslint-disable */
  @ViewChild('iframe', { static: false })
  private readonly iframe: ElementRef;
  private iFrameComponent: IFrameComponent;
  private readonly subscriptions: Subscription;
  private token: string;
  private baseUrl: string;

  constructor(
    private readonly store: Store<State>,
    private readonly route: ActivatedRoute,
    private readonly sanitizer: DomSanitizer,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly zone: NgZone,
    private readonly bookService: BookService,
    private readonly windowCommunicatorService: WindowCommunicatorService
  ) {
    this.subscriptions = new Subscription();
  }

  ngOnInit() {
    this.subscriptions.add(
      this.bookService.getServiceUrl().subscribe(url => {
        this.baseUrl = url;
        this.updateSrc();
      })
    );
    this.subscriptions.add(
      this.store
        .pipe(
          select(getDirectLoginProfile),
          map(profile => profile['epower_token']),
          filter(token => !!token),
          tap(token => {
            this.token = token;
            this.updateSrc();
          }),
          observeOn(asyncScheduler)
        )
        .subscribe(() => {
          this.updateResizer();
          this.subscribeToMessages();
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.zone.runOutsideAngular(this.destroyResizer.bind(this));
  }

  private subscribeToMessages() {
    this.iframe.nativeElement.onload = () => {
      // creating quote after redirect is not supported by ePW yet
      if (this.route.snapshot.queryParams['quotation']) {
        this.windowCommunicatorService
          .observeWithLast([BookMessageTypes.CREATE_QUOTATION])
          .pipe(take(1))
          .subscribe(this.onMessage.bind(this));
      }
      this.subscriptions.add(
        this.windowCommunicatorService
          .observe([BookMessageTypes.CREATE_TASK, BookMessageTypes.SESSION_TIMEOUT, BookMessageTypes.CREATE_QUOTATION])
          .subscribe(this.onMessage.bind(this))
      );
    };
  }

  private updateSrc() {
    const url = new URL(this.baseUrl);
    url.searchParams.append('ADToken', this.token);
    const page = this.route.snapshot.queryParams['path'];
    if (page) {
      const params = new URLSearchParams(page);
      /* eslint-disable */
      params.forEach(function(value, key) {
        url.searchParams.append(key, value);
      });
    }
    this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url.href);
  }

  private updateResizer() {
    this.zone.runOutsideAngular(() => {
      this.destroyResizer();
      const components = iframeResizer(
        {
          heightCalculationMethod: 'bodyOffset',
          checkOrigin: false,
          log: false
        },
        this.iframe.nativeElement
      );
      this.iFrameComponent = components && components.length ? components[0] : null;
    });
    this.changeDetector.detectChanges();
  }

  private postMessage(msg: IWindowMessage) {
    this.iframe.nativeElement.contentWindow.postMessage(msg, this.baseUrl);
  }

  private onMessage(msg: IWindowMessage) {
    switch (msg.type) {
      case BookMessageTypes.CREATE_TASK:
        return;

      case BookMessageTypes.SESSION_TIMEOUT:
        this.updateSrc();
        return;

      case BookMessageTypes.CREATE_QUOTATION:
        this.postMessage(msg);
        return;
    }
  }

  private destroyResizer() {
    if (this.iFrameComponent && this.iFrameComponent.iFrameResizer) {
      this.iFrameComponent.iFrameResizer.close();
    }
  }
}
